import { useState, useEffect, useCallback } from 'react';
import { LocationSettingsContext } from '../context/context';
import { getUserLocation, isGeolocationSupported, checkGeolocationPermission } from '../services/geolocation';
import { getStoredLocation } from '../utils/locationStorage';

interface GeolocationState {
    location: LocationSettingsContext | null;
    isLoading: boolean;
    error: string | null;
    source: 'browser' | 'ip' | 'fallback' | 'stored' | null;
}

export const useGeolocation = () => {
    const [state, setState] = useState<GeolocationState>({
        location: null,
        isLoading: false,
        error: null,
        source: null
    });

    const fetchLocation = useCallback(async () => {
        setState(prev => ({ ...prev, isLoading: true, error: null }));

        try {
            const location = await getUserLocation();
            
            // Check if this came from storage
            const stored = getStoredLocation();
            let source: 'browser' | 'ip' | 'fallback' | 'stored';
            
            if (stored && 
                stored.location.latitude === location.latitude && 
                stored.location.longitude === location.longitude) {
                source = 'stored';
            } else {
                // Determine the source based on the zoom level
                if (location.zoom === 12) {
                    source = 'browser';
                } else if (location.zoom === 8) {
                    source = 'ip';
                } else {
                    source = 'fallback';
                }
            }

            setState({
                location,
                isLoading: false,
                error: null,
                source
            });

            console.log(`Location set from ${source}:`, location);
        } catch (error) {
            setState({
                location: null,
                isLoading: false,
                error: error instanceof Error ? error.message : 'Failed to get location',
                source: null
            });
        }
    }, []);

    const checkSupport = useCallback(async () => {
        const supported = isGeolocationSupported();
        const permission = await checkGeolocationPermission();
        
        // console.log('Geolocation support:', { supported, permission });
        return { supported, permission };
    }, []);

    useEffect(() => {
        // Check support and fetch location on mount
        checkSupport().then(() => {
            fetchLocation();
        });
    }, [checkSupport, fetchLocation]);

    return {
        ...state,
        refetch: fetchLocation,
        checkSupport
    };
}; 